// Step 5 of the client-side X12 pipeline: walks one transaction's flat body
// segments (from step 2's envelope tree) into the loop structure a human
// reader thinks in — header, Name loops, PO1 item loops (with their sub-line,
// nested-Name and transportation sub-loops), and the summary. Only ever runs
// against a transaction whose ST01 is "850"; anything else is left alone so
// it can still be shown, read-only, by the UI.

import type {EdiSegment} from './tokenizer';
import type {EdiEnvelope, EdiGroup, EdiTransaction} from './envelope';

export interface Header850 {
  beg?: EdiSegment;
  /** Not called out in the header shape by name, but a real, dictionary-
   * defined header segment — captured rather than silently dropped. */
  cur?: EdiSegment;
  refs: EdiSegment[];
  per?: EdiSegment;
  tax?: EdiSegment;
  fob?: EdiSegment;
  ctp?: EdiSegment;
  itd?: EdiSegment;
  dis?: EdiSegment;
  inc?: EdiSegment;
  dtm: EdiSegment[];
  ldt?: EdiSegment;
  msg: EdiSegment[];
  iss?: EdiSegment;
  pct?: EdiSegment;
  pwk: EdiSegment[];
  lm: EdiSegment[];
  lq: EdiSegment[];
}

export interface NameLoop850 {
  n1: EdiSegment;
  n2?: EdiSegment;
  n3?: EdiSegment;
  n4?: EdiSegment;
  n5?: EdiSegment;
  n6?: EdiSegment;
  n9: EdiSegment[];
  refs: EdiSegment[];
  per?: EdiSegment;
}

export interface ItemNameSubLoop850 {
  n1: EdiSegment;
  n2?: EdiSegment;
  n3?: EdiSegment;
  n4?: EdiSegment;
}

export interface ItemSubLine850 {
  sln: EdiSegment;
}

export interface ItemTransportation850 {
  tc1: EdiSegment;
}

export interface ItemLoop850 {
  po1: EdiSegment;
  mea: EdiSegment[];
  pid: EdiSegment[];
  pwk: EdiSegment[];
  pkg: EdiSegment[];
  po3?: EdiSegment;
  ctp?: EdiSegment;
  refs: EdiSegment[];
  per?: EdiSegment;
  sac: EdiSegment[];
  it8?: EdiSegment;
  ctb?: EdiSegment;
  tax?: EdiSegment;
  fob?: EdiSegment;
  sdq: EdiSegment[];
  dtm: EdiSegment[];
  ldt?: EdiSegment;
  iss?: EdiSegment;
  pam: EdiSegment[];
  msg: EdiSegment[];
  sln?: ItemSubLine850;
  schedule: EdiSegment[];
  csh: EdiSegment[];
  tc2: EdiSegment[];
  pct: EdiSegment[];
  transportation?: ItemTransportation850;
  subNameLoops: ItemNameSubLoop850[];
}

export interface Summary850 {
  ctt?: EdiSegment;
  amt: EdiSegment[];
}

export interface Parsed850 {
  header: Header850;
  nameLoops: NameLoop850[];
  items: ItemLoop850[];
  summary: Summary850;
}

function attachHeaderSegment(header: Header850, s: EdiSegment): void {
  switch (s.id) {
    case 'BEG':
      header.beg = s;
      break;
    case 'CUR':
      header.cur = s;
      break;
    case 'REF':
      header.refs.push(s);
      break;
    case 'PER':
      header.per = s;
      break;
    case 'TAX':
      header.tax = s;
      break;
    case 'FOB':
      header.fob = s;
      break;
    case 'CTP':
      header.ctp = s;
      break;
    case 'ITD':
      header.itd = s;
      break;
    case 'DIS':
      header.dis = s;
      break;
    case 'INC':
      header.inc = s;
      break;
    case 'DTM':
      header.dtm.push(s);
      break;
    case 'LDT':
      header.ldt = s;
      break;
    case 'MSG':
      header.msg.push(s);
      break;
    case 'ISS':
      header.iss = s;
      break;
    case 'PCT':
      header.pct = s;
      break;
    case 'PWK':
      header.pwk.push(s);
      break;
    case 'LM':
      header.lm.push(s);
      break;
    case 'LQ':
      header.lq.push(s);
      break;
    default:
      break;
  }
}

function attachNameLoopSegment(loop: NameLoop850, s: EdiSegment): void {
  switch (s.id) {
    case 'N2':
      loop.n2 = s;
      break;
    case 'N3':
      loop.n3 = s;
      break;
    case 'N4':
      loop.n4 = s;
      break;
    case 'N5':
      loop.n5 = s;
      break;
    case 'N6':
      loop.n6 = s;
      break;
    case 'N9':
      loop.n9.push(s);
      break;
    case 'REF':
      loop.refs.push(s);
      break;
    case 'PER':
      loop.per = s;
      break;
    default:
      break;
  }
}

function attachItemNameSubLoopSegment(
  loop: ItemNameSubLoop850,
  s: EdiSegment
): void {
  switch (s.id) {
    case 'N2':
      loop.n2 = s;
      break;
    case 'N3':
      loop.n3 = s;
      break;
    case 'N4':
      loop.n4 = s;
      break;
    default:
      break;
  }
}

function attachItemSegment(item: ItemLoop850, s: EdiSegment): void {
  switch (s.id) {
    case 'MEA':
      item.mea.push(s);
      break;
    case 'PID':
      item.pid.push(s);
      break;
    case 'PWK':
      item.pwk.push(s);
      break;
    case 'PKG':
      item.pkg.push(s);
      break;
    case 'PO3':
      item.po3 = s;
      break;
    case 'CTP':
      item.ctp = s;
      break;
    case 'REF':
      item.refs.push(s);
      break;
    case 'PER':
      item.per = s;
      break;
    case 'SAC':
      item.sac.push(s);
      break;
    case 'IT8':
      item.it8 = s;
      break;
    case 'CTB':
      item.ctb = s;
      break;
    case 'TAX':
      item.tax = s;
      break;
    case 'FOB':
      item.fob = s;
      break;
    case 'SDQ':
      item.sdq.push(s);
      break;
    case 'DTM':
      item.dtm.push(s);
      break;
    case 'LDT':
      item.ldt = s;
      break;
    case 'ISS':
      item.iss = s;
      break;
    case 'PAM':
      item.pam.push(s);
      break;
    case 'MSG':
      item.msg.push(s);
      break;
    case 'SCH':
      item.schedule.push(s);
      break;
    case 'CSH':
      item.csh.push(s);
      break;
    case 'TC2':
      item.tc2.push(s);
      break;
    case 'PCT':
      item.pct.push(s);
      break;
    default:
      break;
  }
}

function emptyHeader(): Header850 {
  return {refs: [], dtm: [], msg: [], pwk: [], lm: [], lq: []};
}

function emptyItem(po1: EdiSegment): ItemLoop850 {
  return {
    po1,
    mea: [],
    pid: [],
    pwk: [],
    pkg: [],
    refs: [],
    sac: [],
    sdq: [],
    dtm: [],
    pam: [],
    msg: [],
    schedule: [],
    csh: [],
    tc2: [],
    pct: [],
    subNameLoops: [],
  };
}

type Phase = 'header' | 'name' | 'item' | 'summary';
type ItemSubPhase = 'main' | 'name' | 'subline' | 'transportation';

/**
 * Walks one 850 transaction's flat body segments (ST/SE excluded) into its
 * loop structure. Never throws — a segment that doesn't fit anywhere
 * sensible in the current loop is simply not attached, rather than the walk
 * aborting; the validator is what reports on a document that's missing or
 * misordered.
 */
export function parse850Segments(segments: EdiSegment[]): Parsed850 {
  const header = emptyHeader();
  const nameLoops: NameLoop850[] = [];
  const items: ItemLoop850[] = [];
  const summary: Summary850 = {amt: []};

  let phase: Phase = 'header';
  let itemSubPhase: ItemSubPhase = 'main';
  let currentNameLoop: NameLoop850 | undefined;
  let currentItem: ItemLoop850 | undefined;
  let currentItemNameSubLoop: ItemNameSubLoop850 | undefined;

  for (const s of segments) {
    switch (s.id) {
      case 'N1':
        if (phase === 'item' && currentItem) {
          itemSubPhase = 'name';
          currentItemNameSubLoop = {n1: s};
          currentItem.subNameLoops.push(currentItemNameSubLoop);
        } else {
          phase = 'name';
          currentNameLoop = {n1: s, n9: [], refs: []};
          nameLoops.push(currentNameLoop);
        }
        break;

      case 'N2':
      case 'N3':
      case 'N4':
      case 'N5':
      case 'N6':
      case 'N9':
        if (itemSubPhase === 'name' && currentItemNameSubLoop) {
          attachItemNameSubLoopSegment(currentItemNameSubLoop, s);
        } else if (phase === 'name' && currentNameLoop) {
          attachNameLoopSegment(currentNameLoop, s);
        }
        break;

      case 'PO1':
        phase = 'item';
        itemSubPhase = 'main';
        currentItemNameSubLoop = undefined;
        currentItem = emptyItem(s);
        items.push(currentItem);
        break;

      case 'SLN':
        if (currentItem) {
          itemSubPhase = 'subline';
          currentItem.sln = {sln: s};
        }
        break;

      case 'TC1':
        if (currentItem) {
          itemSubPhase = 'transportation';
          currentItem.transportation = {tc1: s};
        }
        break;

      case 'CTT':
        phase = 'summary';
        summary.ctt = s;
        currentItem = undefined;
        currentNameLoop = undefined;
        currentItemNameSubLoop = undefined;
        break;

      case 'AMT':
        summary.amt.push(s);
        break;

      default:
        if (phase === 'header') {
          attachHeaderSegment(header, s);
        } else if (phase === 'name' && currentNameLoop) {
          attachNameLoopSegment(currentNameLoop, s);
        } else if (phase === 'item' && currentItem) {
          attachItemSegment(currentItem, s);
        }
        break;
    }
  }

  return {header, nameLoops, items, summary};
}

export interface EdiTransactionWithParsed850 extends EdiTransaction {
  /** Left undefined for a transaction whose ST01 isn't "850" — it keeps only
   * its raw segment list, for read-only display. */
  parsed850?: Parsed850;
}

export interface EdiGroupWithParsed850 extends Omit<EdiGroup, 'transactions'> {
  transactions: EdiTransactionWithParsed850[];
}

export interface EdiEnvelopeWithParsed850 extends Omit<EdiEnvelope, 'groups'> {
  groups: EdiGroupWithParsed850[];
}

/**
 * Parses every 850 transaction in an envelope tree (step 2's output),
 * attaching `parsed850` to each. A non-850 transaction is passed through
 * unchanged, `parsed850` left undefined.
 */
export function attachParsed850(
  envelope: EdiEnvelope
): EdiEnvelopeWithParsed850 {
  return {
    ...envelope,
    groups: envelope.groups.map(group => ({
      ...group,
      transactions: group.transactions.map(transaction => ({
        ...transaction,
        parsed850:
          transaction.transactionSetCode === '850'
            ? parse850Segments(transaction.segments)
            : undefined,
      })),
    })),
  };
}
